import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, EyeIcon, GlobeIcon, ShieldCheckIcon, UsersIcon, VideoIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Container } from '../../components/ui/Container';
import { GeometricAccent } from '../../components/ui/GeometricAccent';
import { MosqueArchMask } from '../../components/ui/MosqueArchMask';
import { PatternDecor } from '../../components/ui/PatternDecor';
type Tone = 'sage' | 'sky' | 'rose' | 'sand';
const toneClasses: Record<Tone, string> = {
  sage: 'border-forest-700/10 bg-sage-100',
  sky: 'border-[#C7D4E0]/40 bg-[#EDF2F7]',
  rose: 'border-[#E0BFB3]/40 bg-[#F7EDE9]',
  sand: 'border-sand-300/40 bg-sand-100'
};
const sessionStructure = [{
  step: '01',
  title: 'الترحيب والتواصل',
  description: 'بداية قصيرة تساعد الأطفال على الاستقرار والشعور بأنهم مرئيون ومسموعون.'
}, {
  step: '02',
  title: 'فقرة تعلّم موجهة',
  description: 'درس مناسب للعمر يجمع بين النقاش والمشاركة الفعّالة.'
}, {
  step: '03',
  title: 'التأمل والختام',
  description: 'تأمل موجز يرسّخ ما تم تعلّمه في الحياة اليومية.'
}];
const features = [{
  icon: UsersIcon,
  title: 'مجموعات صغيرة',
  description: 'من 8 إلى 10 أطفال في الجلسة، لمشاركة وتواصل حقيقيين.',
  tone: 'sage' as const
}, {
  icon: VideoIcon,
  title: 'مباشر وتفاعلي',
  description: 'نقاشات وأنشطة موجهة، لا وقتًا سلبيًا أمام الشاشة.',
  tone: 'sky' as const
}, {
  icon: ShieldCheckIcon,
  title: 'آمن بحكم التصميم',
  description: 'جلسات خاضعة للإشراف، مدمجة فيها بروتوكولات حماية الطفل.',
  tone: 'rose' as const
}, {
  icon: EyeIcon,
  title: 'إطلاع الوالدين',
  description: 'تحديثات واضحة، وإمكانية الوصول إلى التسجيلات، وملاحظات منتظمة للوالدين.',
  tone: 'sand' as const
}];
const practicalDetails = [{
  icon: ClockIcon,
  label: 'مدة الجلسة',
  value: '45 إلى 60 دقيقة'
}, {
  icon: GlobeIcon,
  label: 'المناطق الزمنية',
  value: 'مواعيد متعددة عبر المناطق'
}, {
  icon: UsersIcon,
  label: 'حجم المجموعة',
  value: 'من 8 إلى 10 أطفال'
}];
export function ArabicOnlineLearning() {
  return <div dir="rtl" className="overflow-hidden text-right" data-id="element-3039" data-mp="5l2990z2nmpw">
      <section className="relative overflow-hidden" data-id="element-3040" data-mp="5nogezi1w07l">
        <GeometricAccent variant="arabesque" className="absolute -right-20 top-6 hidden h-[420px] w-[420px] md:block" opacity={0.08} data-id="element-3041" data-mp="9nfbvgiiv4ig" />
        <GeometricAccent variant="dots" className="absolute left-8 top-16 h-32 w-32" opacity={0.4} data-id="element-3042" data-mp="em03wsnmehvt" />
        <Container className="relative pb-16 pt-8 md:pb-24 md:pt-12" data-id="element-3043" data-mp="jjigct0ujf8k">
          <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-8" data-id="element-3044" data-mp="od4ihr9dw0q4">
            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} className="flex flex-col gap-6 lg:col-span-6" data-id="element-3045" data-mp="tbo4frnz9cih">
              <span className="text-[15px] font-semibold text-forest-700" data-id="element-3046" data-mp="12bkx04dhmvit">مباشر وآمن ومصمم للأسر المسلمة الحديثة حول العالم</span>
              <h1 className="font-display text-4xl font-semibold leading-[1.45] tracking-tight text-ink-900 sm:text-5xl lg:text-[3.5rem]" data-id="element-3047" data-mp="2muogaf3fscto">التعلّم عبر الإنترنت</h1>
              <div className="max-w-xl space-y-3 text-base leading-loose text-ink-700 md:text-lg" data-id="element-3048" data-mp="2dsxa794zi3za">
                <p data-id="element-3049" data-mp="28udpfc24my8i">يتم تعلّم أطفالنا عبر الإنترنت حتى تتمكن الأسر من الوصول إلى دعم مستمر أينما كانوا.</p>
                <p data-id="element-3050" data-mp="35kwfim40mj4l">نركز على التفاعل المباشر والمجموعات الصغيرة والبيئة المحترمة التي يشعر فيها كل طفل بأنه مرئي ومحمي ومشجَّع.</p>
              </div>
              <div className="flex flex-wrap gap-3 pt-2" data-id="element-3051" data-mp="31jkxb1nh106f">
                <Link to="/ar/children/kids-programs" data-id="element-3052" data-mp="2wr9mv4v3tvzm"><Button variant="primary" size="lg" data-id="element-3053" data-mp="2wq8a8vyfx1fs">استكشف البرامج</Button></Link>
                <Link to="/ar/children/scholarships" data-id="element-3054" data-mp="2rs6qs9x3r03s"><Button variant="secondary" size="lg" data-id="element-3055" data-mp="2rr5g46k00fml">المنح والدعم</Button></Link>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.1,
            ease: [0.22, 1, 0.36, 1]
          }} className="relative lg:col-span-6" data-id="element-3056" data-mp="3khx7mqq4cg3l">
              <div className="relative aspect-[200/148] w-full lg:-mr-8 lg:w-[calc(100%+6rem)]" data-id="element-3057" data-mp="391qvzuifclib">
                <MosqueArchMask src="https://cdn.magicpatterns.com/uploads/jjNQUzfbm3fVgHUYbPU5fW/online-learning.jpg" alt="طفلة مبتسمة في درس مباشر عبر الإنترنت مع زملائها" data-id="element-3058" data-mp="2v1py8nxghu72" />
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="bg-sage-50/60 py-16 md:py-24" data-id="element-3059" data-mp="3ns860294kjqt">
        <Container data-id="element-3060" data-mp="3rqnu69gxzy5t">
          <h2 className="max-w-3xl font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3061" data-mp="3rqxilsmc9x9e">بنية بسيطة يعتاد عليها الأطفال بسرعة</h2>
          <p className="mt-5 max-w-3xl leading-loose text-ink-700" data-id="element-3062" data-mp="3iyvjbxo0an0p">صُمم نموذجنا للانتباه والسلامة والمشاركة الحقيقية، لا للجلوس السلبي أمام الشاشة.</p>
          <div className="mt-10 grid gap-5 md:grid-cols-3" data-id="element-3063" data-mp="aiakh9p39abb">
            {sessionStructure.map((item, index) => <motion.article key={item.step} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.08
          }} className="rounded-2xl border border-sage-200 bg-white p-6 shadow-soft" data-id="element-3064" data-mp="fc51g5db7b6p" data-mpi={index}>
                <span className="font-display text-3xl font-semibold text-sand-400" data-id="element-3065" data-mp="ye5ffytcu8gw">{item.step}</span>
                <h3 className="mt-3 font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3066" data-mp="tgm16ywnpvoy">{item.title}</h3>
                <p className="mt-2 text-sm leading-loose text-ink-700" data-id="element-3067" data-mp="5s2u2mpcfoyg">{item.description}</p>
              </motion.article>)}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-3068" data-mp="fmcpyedhv5dx">
        <PatternDecor side="left" y="top" size="lg" data-id="element-3069" data-mp="ao1g43n78uvp" />
        <Container data-id="element-3070" data-mp="1r32wjyltxz82">
          <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3071" data-mp="1r2b3ovjmaflh">ما الذي يميز تعلّمنا عبر الإنترنت</h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2" data-id="element-3072" data-mp="1m6vr06xcipqn">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <motion.article key={feature.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.5,
              delay: index * 0.06
            }} className={`flex gap-4 rounded-2xl border p-6 ${toneClasses[feature.tone]}`} data-id="element-3073" data-mp="1y57xa01gwupq" data-mpi={index}>
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-forest-700/10 bg-white text-forest-700" data-id="element-3074" data-mp="1aiszwmeqih7f"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-3075" data-mp="1kfe6vdabe2dw" /></span>
                  <div data-id="element-3076" data-mp="1kdb741jd0uvz"><h3 className="font-display text-lg font-semibold leading-loose text-ink-900" data-id="element-3077" data-mp="1kdky9sg55839">{feature.title}</h3><p className="mt-1 text-sm leading-loose text-ink-700" data-id="element-3078" data-mp="1p7xja6x9mrh4">{feature.description}</p></div>
                </motion.article>;
          })}
          </div>
        </Container>
      </section>

      <section className="bg-[#F7EDE9]/60 py-16 md:py-24" data-id="element-3079" data-mp="p6aiowxxi51y">
        <Container data-id="element-3080" data-mp="p5shrn0bn2np">
          <div className="grid gap-5 md:grid-cols-3" data-id="element-3081" data-mp="p47tug8iihyt">
            {practicalDetails.map(detail => {
            const Icon = detail.icon;
            return <article key={detail.label} className="rounded-2xl border border-sage-200 bg-white p-6 text-center shadow-soft" data-id="element-3082" data-mp="f6tjmyd6mpey">
                  <span className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-sage-100 text-forest-700" data-id="element-3083" data-mp="ab4gym5v8sef"><Icon size={18} strokeWidth={1.6} aria-hidden="true" data-id="element-3084" data-mp="1gq1aihfbfus" /></span>
                  <p className="mt-4 text-xs font-semibold text-forest-700" data-id="element-3085" data-mp="1en1j6qgy8cv">{detail.label}</p>
                  <p className="mt-2 font-display text-xl font-semibold leading-loose text-ink-900" data-id="element-3086" data-mp="3skwu5khi4vg1">{detail.value}</p>
                </article>;
          })}
          </div>
        </Container>
      </section>

      <section className="relative isolate overflow-hidden bg-white/60 py-16 md:py-24" data-id="element-3087" data-mp="3en86k4w1va7a">
        <PatternDecor side="right" y="middle" size="md" data-id="element-3088" data-mp="3epm2sh2gusgf" />
        <Container data-id="element-3089" data-mp="16fv1jkc6boyy">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between" data-id="element-3090" data-mp="16fbg3tnbeig6">
            <div className="max-w-xl" data-id="element-3091" data-mp="1bd4pevjwzsko">
              <h2 className="font-display text-3xl font-semibold leading-[1.45] text-ink-900 md:text-5xl" data-id="element-3092" data-mp="wgmap0cluqgt">هل أنتم مستعدون للبدء؟</h2>
              <p className="mt-4 leading-loose text-ink-700" data-id="element-3093" data-mp="11efgvtdwrkh3">اختر برنامجًا يناسب عمر طفلك واحتياجاته، وابدأ ببناء الثقة والقيم والانتماء في مساحة تعليمية آمنة.</p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-3094" data-mp="sfaiqo69dt5g">
              <Link to="/ar/children/kids-programs" data-id="element-3095" data-mp="sg3iv6appkem"><Button variant="primary" data-id="element-3096" data-mp="dq4lh5qzed07">عرض برامج الأطفال</Button></Link>
              <Link to="/ar/children/scholarships" data-id="element-3097" data-mp="do1kjqoaizgp"><Button variant="secondary" data-id="element-3098" data-mp="iofuxfdd6lhw">التقدّم بطلب منحة</Button></Link>
            </div>
          </div>
        </Container>
      </section>
    </div>;
}